import React, { useState } from 'react';
import { format, addMonths, subMonths } from 'date-fns';
import CalendarGrid from './CalendarGrid';
import TaskModal from '../Task/TaskModal';
import Button from '../Common/Button';
import FinishModeBanner from '../Common/FinishModeBanner';
import { useTasks } from '../../hooks/useTasks';
import { useCalendar } from '../../hooks/useCalendar';
import { useCalendars } from '../../contexts/CalendarContext';
import { useSwipeGesture } from '../../hooks/useMobile';

const CalendarView = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedTask, setSelectedTask] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('view'); // 'view', 'add', 'edit'
    const [selectedDate, setSelectedDate] = useState(null);

    const { selectedCalendar } = useCalendars();
    const { tasks, loading, createTask, updateTask, deleteTask, refreshTasks } = useTasks(selectedCalendar?.id);
    const { daysInMonth } = useCalendar(currentDate);

    const handlePreviousMonth = () => {
        setCurrentDate(subMonths(currentDate, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(addMonths(currentDate, 1));
    };

    // Add swipe gesture support for mobile
    const swipeHandlers = useSwipeGesture(
        handleNextMonth,  // Swipe left = next month
        handlePreviousMonth  // Swipe right = previous month
    );

    const handleToday = () => {
        setCurrentDate(new Date());
    };

    const handleAddTask = (date) => {
        setSelectedDate(date);
        setSelectedTask(null);
        setModalMode('add');
        setIsModalOpen(true);
    };

    const handleTaskClick = (task) => {
        setSelectedTask(task);
        setModalMode('view');
        setIsModalOpen(true);
    };

    const handleEditTask = () => {
        setModalMode('edit');
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTask(null);
        setSelectedDate(null);
        setModalMode('view');
    };

    const handleSaveTask = async (taskData) => {
        // Ensure we have a selected calendar
        if (modalMode === 'add' && !selectedCalendar?.id) {
            throw new Error('Please select a calendar first.');
        }

        if (modalMode === 'add') {
            await createTask(taskData, selectedCalendar.id);
        } else if (modalMode === 'edit') {
            await updateTask(selectedTask.id, taskData);
        }

        handleCloseModal();
        refreshTasks();
    };

    const handleDeleteTask = async () => {
        if (!selectedTask) return;

        if (window.confirm('Are you sure you want to delete this task?')) {
            try {
                await deleteTask(selectedTask.id);
                handleCloseModal();
                refreshTasks();
            } catch (error) {
                console.error('Error deleting task:', error);
                alert('Failed to delete task. Please try again.');
            }
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-2 sm:p-4 md:p-6 transition-colors duration-200">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-3 sm:mb-4 md:mb-6">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                    <span className="hidden sm:inline">{format(currentDate, 'MMMM yyyy')}</span>
                    <span className="sm:hidden">{format(currentDate, 'MMM yyyy')}</span>
                </h2>

                <div className="flex items-center space-x-1 md:space-x-2">
                    <Button
                        onClick={handlePreviousMonth}
                        variant="ghost"
                        ariaLabel="Previous month"
                        className="touch-manipulation min-h-[44px] min-w-[44px] p-2"
                    >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </Button>

                    <Button
                        onClick={handleToday}
                        variant="secondary"
                        className="touch-manipulation min-h-[44px] text-xs sm:text-sm px-2 sm:px-3"
                    >
                        Today
                    </Button>

                    <Button
                        onClick={handleNextMonth}
                        variant="ghost"
                        ariaLabel="Next month"
                        className="touch-manipulation min-h-[44px] min-w-[44px] p-2"
                    >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </Button>
                </div>
            </div>

            {/* Finish Mode Notification Banner */}
            <FinishModeBanner />

            {/* Loading State */}
            {loading && (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400" role="status" aria-label="Loading"></div>
                    <p className="mt-2 text-gray-600 dark:text-gray-300">Loading tasks...</p>
                </div>
            )}

            {/* Calendar Grid - with swipe gesture support */}
            {!loading && (
                <div {...swipeHandlers}>
                    <CalendarGrid
                        daysInMonth={daysInMonth}
                        tasks={tasks}
                        onAddTask={handleAddTask}
                        onTaskClick={handleTaskClick}
                    />
                </div>
            )}

            {/* Task Modal */}
            {isModalOpen && (
                <TaskModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    task={selectedTask}
                    mode={modalMode}
                    onSave={handleSaveTask}
                    onEdit={handleEditTask}
                    onDelete={handleDeleteTask}
                    initialDate={selectedDate}
                />
            )}
        </div>
    );
};

export default CalendarView;